import { ChevronRightIcon } from '@radix-ui/react-icons';

import { useThemeContext } from '../../contexts/ThemeContext';
import { resolveColor } from '../customTheme/theme/utils/resolveColor';
import { PoweredByQITech } from '../customTheme/PrivatePayrollAuction/components/PoweredByQITech';
import QISignTheme from '../../interfaces/Theme';
import * as S from './styles';

type ListingStatus =
    | 'not_started'
    | 'in_progress'
    | 'accepted'
    | 'declined'
    | 'completed'
    | 'canceled'
    | 'expired';

interface CreditFields {
    financed_amount?: number;
    issue_amount?: number;
    installments?: number;
    number_of_installments?: number;
}

interface RefinancingOperation {
    new_contract?: {
        credit_operation?: CreditFields;
    };
}

interface ProposalEnvelope {
    envelope_key: string;
    listing_status: ListingStatus | string;
    title?: string;
    subject?: string;
    envelope_subject?: string;
    credit?: CreditFields;
    credit_operation_data?: CreditFields;
    credit_operation_summary?: CreditFields;
    refinancing_operation?: RefinancingOperation;
}

interface EnvelopeFolderProposalsProps {
    listing: any;
    resolvedTheme: QISignTheme;
    onSelectProposal: (envelope: ProposalEnvelope) => void;
    onCommit: () => void;
}

type StatusTone = 'pending' | 'progress' | 'done' | 'declined' | 'terminal';

const STATUS_LABELS: Record<string, string> = {
    not_started: 'pendente',
    in_progress: 'em andamento',
    accepted: 'pronto',
    declined: 'recusado',
    completed: 'concluído',
    canceled: 'cancelado',
    expired: 'expirado',
};

const STATUS_TONES: Record<string, StatusTone> = {
    not_started: 'pending',
    in_progress: 'progress',
    accepted: 'done',
    declined: 'declined',
    completed: 'terminal',
    canceled: 'terminal',
    expired: 'terminal',
};

const DECIDED_STATUSES = ['accepted', 'declined', 'completed', 'canceled', 'expired'];

const formatCurrency = (value?: number): string =>
    value == null
        ? '-'
        : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

const statusTone = (status: string): StatusTone => STATUS_TONES[status] ?? 'terminal';

export const EnvelopeFolderProposals = (props: EnvelopeFolderProposalsProps) => {
    const { listing, resolvedTheme, onSelectProposal, onCommit } = props;

    const { clientThemeSettings } = useThemeContext();
    const theme: QISignTheme = clientThemeSettings ?? resolvedTheme;
    const primary = resolveColor('primary', theme);

    const envelopes: ProposalEnvelope[] = Array.isArray(listing?.envelopes) ? listing.envelopes : [];
    const completionAvailable = listing?.completion_available === true;

    const totalCount = envelopes.length;
    const decidedCount = envelopes.filter((envelope) =>
        DECIDED_STATUSES.includes(envelope.listing_status),
    ).length;
    const progressPct = totalCount === 0 ? 0 : Math.round((decidedCount / totalCount) * 100);

    const firstPendingEnvelope = envelopes.find(
        (envelope) => envelope.listing_status === 'not_started',
    );
    const showNextProposal = !completionAvailable && Boolean(firstPendingEnvelope);

    const proposalAmount = (envelope: ProposalEnvelope): string => {
        const credit = envelope.credit ?? envelope.credit_operation_data ?? {};
        const summary = envelope.credit_operation_summary ?? {};
        const refinancingIssueAmount =
            envelope.refinancing_operation?.new_contract?.credit_operation?.issue_amount;
        return formatCurrency(
            credit.issue_amount ?? summary.issue_amount ?? credit.financed_amount ?? refinancingIssueAmount,
        );
    };

    const proposalTitle = (envelope: ProposalEnvelope): string =>
        envelope.subject ?? envelope.envelope_subject ?? envelope.title ?? 'Proposta de crédito';

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {theme.logo_url && <S.LogoImage src={theme.logo_url} alt="logo" />}
            </S.LogoSection>

            <S.ScrollContent>
                <S.HeaderBlock>
                    <S.Title $color={primary}>Suas propostas</S.Title>
                    <S.Subtitle>
                        Você tem {totalCount} {totalCount === 1 ? 'proposta' : 'propostas'} para revisar e assinar.
                    </S.Subtitle>
                </S.HeaderBlock>

                <S.ProgressBlock>
                    <S.ProgressRow>
                        <S.ProgressLabel>Progresso</S.ProgressLabel>
                        <S.ProgressCount $color={primary}>
                            <strong>{decidedCount}</strong> de {totalCount} decididas
                        </S.ProgressCount>
                    </S.ProgressRow>
                    <S.ProgressTrack>
                        <S.ProgressFill $color={primary} $pct={progressPct} />
                    </S.ProgressTrack>
                </S.ProgressBlock>

                {totalCount === 0 ? (
                    <S.EmptyState>Nenhuma proposta disponível no momento.</S.EmptyState>
                ) : (
                    <S.List>
                        {envelopes.map((envelope) => (
                            <S.ListItem
                                key={envelope.envelope_key}
                                $color={primary}
                                onClick={() => onSelectProposal(envelope)}
                            >
                                <S.ItemTitle>{proposalTitle(envelope)}</S.ItemTitle>
                                <S.ItemMetaRow>
                                    <S.ItemSummary>{proposalAmount(envelope)}</S.ItemSummary>
                                    <S.ItemMetaRight>
                                        <S.StatusBadge $tone={statusTone(envelope.listing_status)}>
                                            {statusLabel(envelope.listing_status)}
                                        </S.StatusBadge>
                                        <S.Chevron $color={primary}>
                                            <ChevronRightIcon width={20} height={20} />
                                        </S.Chevron>
                                    </S.ItemMetaRight>
                                </S.ItemMetaRow>
                            </S.ListItem>
                        ))}
                    </S.List>
                )}
            </S.ScrollContent>

            <S.Footer>
                {showNextProposal ? (
                    <S.CommitButton
                        $color={primary}
                        $disabled={false}
                        onClick={() => onSelectProposal(firstPendingEnvelope as ProposalEnvelope)}
                    >
                        Ver próxima proposta
                    </S.CommitButton>
                ) : (
                    <S.CommitButton
                        $color={primary}
                        $disabled={!completionAvailable}
                        disabled={!completionAvailable}
                        aria-disabled={!completionAvailable}
                        onClick={completionAvailable ? onCommit : undefined}
                    >
                        Concluir
                    </S.CommitButton>
                )}
                <PoweredByQITech />
            </S.Footer>
        </S.PageWrapper>
    );
};
